import { motion } from 'framer-motion';
import { useState } from 'react';

const PolicyTimeline = () => {
  const [activeNode, setActiveNode] = useState<number | null>(null);

  const timelineData = [
    {
      year: "2014",
      title: "义乌试点启动",
      description: "国内首个市场采购贸易方式试点在义乌启动",
      detail: "市场采购贸易方式正式成为国家级试点政策，为小商品出口开辟新通道"
    },
    {
      year: "2016-2019",
      title: "试点扩展期",
      description: "试点城市逐步扩展至全国多个重要商贸城市",
      detail: "海宁、常熟、临沂等14个市场纳入试点范围，政策逐步成熟"
    },
    {
      year: "2020以后",
      title: "常态化运行",
      description: "全国多地实现市场采购贸易常态化运营",
      detail: "政策体系完善，监管流程标准化，成为稳定的贸易方式选择"
    },
    {
      year: "广州白云",
      title: "白云区试点",
      description: "广州白云区正式纳入市场采购贸易试点体系",
      detail: "依托白云区16个专业市场，提供华南地区便利化出口服务"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-primary mb-4">政策发展历程</h2>
          <p className="text-gray-600 text-lg">非临时政策·非灰色通道·持续发展十年</p>
        </motion.div>

        {/* 桌面端横向时间轴 */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* 时间轴线条 */}
            <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-accent to-primary"></div>
            
            <div className="grid grid-cols-4 gap-8">
              {timelineData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="relative text-center"
                  onMouseEnter={() => setActiveNode(index)}
                  onMouseLeave={() => setActiveNode(null)}
                >
                  {/* 时间节点 */}
                  <div className={`relative z-10 w-16 h-16 mx-auto mb-6 rounded-full border-4 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                    activeNode === index 
                      ? 'bg-accent border-accent scale-110 shadow-lg' 
                      : 'bg-white border-primary hover:border-accent hover:scale-105'
                  }`}>
                    <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      activeNode === index ? 'bg-white' : 'bg-primary'
                    }`}></div>
                  </div>

                  {/* 年份 */}
                  <div className="bg-primary text-white font-bold px-3 py-1 rounded-full text-sm mb-3 inline-block">
                    {item.year}
                  </div>

                  {/* 内容卡片 */}
                  <div className={`bg-gray-50 rounded-lg p-4 transition-all duration-300 ${
                    activeNode === index ? 'shadow-lg transform scale-105 bg-accent/10 border border-accent/20' : ''
                  }`}>
                    <h3 className="font-semibold text-primary mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {activeNode === index ? item.detail : item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* 移动端纵向时间轴 */}
        <div className="lg:hidden space-y-8">
          {timelineData.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative flex gap-4"
            >
              {/* 时间线和节点 */}
              <div className="flex flex-col items-center">
                <div className="w-4 h-4 bg-accent rounded-full border-4 border-white shadow-lg"></div>
                {index < timelineData.length - 1 && (
                  <div className="w-0.5 h-16 bg-gray-300 mt-2"></div>
                )}
              </div>

              {/* 内容 */}
              <div className="flex-1 pb-8">
                <div className="bg-primary text-white font-bold px-3 py-1 rounded-full text-sm mb-3 inline-block">
                  {item.year}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-primary mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 总结说明 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <i className="ri-shield-check-line text-accent text-xl"></i>
              <span className="font-semibold text-primary">政策稳定性保障</span>
            </div>
            <p className="text-gray-700 leading-relaxed max-w-3xl mx-auto">
              十年发展历程证明，市场采购贸易1039已成为国家长期支持的正规出口模式，
              不是临时性政策，更不是灰色通道。政策体系完善，监管规范，为企业提供稳定可靠的出口选择。
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PolicyTimeline;